import type { FormStore } from "./Form.js";
import type { Schema } from "shelving";
import { useFormContext } from "./Form.js";

/** Properties accepted by all fields. */
export type FieldProps = {
	readonly title?: string | undefined;
	readonly description?: string | undefined;
	readonly placeholder?: string | undefined;
	readonly mods?: string | undefined;
};

/** Properties specifying a named field. */
export type NamedFieldProps = { name: string } & FieldProps;

/** Data about a field. */
export type FieldData<T> = NamedFieldProps & {
	form: FormStore<{ [key: string]: T }>;
	schema: Schema<T>;
	fullplaceholder: string | undefined;
	value: T | undefined;
};

/** A field wrapper element with a title, description, and error message. */
export function Field({
	title, //
	description,
	children,
	mods = "",
}: {
	name?: string | undefined;
	title?: string | undefined;
	description?: string | undefined;
	children: React.ReactNode;
	mods?: string | undefined;
}): React.ReactElement {
	return (
		<section className={`field ${mods}`}>
			{title ? <h3 className={`field-title label space-xxsmall smush`}>{title}</h3> : null}
			{description ? <p className="field-title color-quiet size-small space-xxsmall smush">{description}</p> : null}
			{children}
		</section>
	);
}

/** Function that returns the information we need for a field. */
export function useField<T>(props: NamedFieldProps): FieldData<T>;
export function useField(props: NamedFieldProps): FieldData<unknown>;
export function useField(props: NamedFieldProps): FieldData<unknown> {
	const name = props.name;
	const form = useFormContext();
	const schema = form.getSchema(name);
	const title = props.title ?? schema.title;
	const placeholder = props.placeholder ?? schema.placeholder;
	return {
		...props,
		form,
		schema,
		title,
		placeholder,
		fullplaceholder: title && placeholder ? `${title} (${placeholder})` : title || placeholder,
		description: props.description ?? schema.description,
		value: form.value[name] ?? schema.value,
	};
}
